import {
  Button,
  Checkbox,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ManagedConfiguration } from '../../../hooks/api/useNswagClient';
import { TableColumn, TableSectionProps, TableSortDirection } from '../../../components/shared/tables/TableSection';
import { hasSpecialistEditor } from './specialistEditors';
import { dropdownOptions, enumLabel } from '../enums/dropdownEnums';

export type ConfigurationScope = 'Franchise' | 'Store';

type Translate = (key: string, options?: Record<string, unknown>) => string;

// The locale files are the only source of a setting's name and description; the catalogue carries
// none. A missing key renders as the raw key, and the locale completeness test fails the build.
export const localisedName = (t: Translate, item: ManagedConfiguration): string =>
  t(`configurations.${item.name}`);

export const localisedDescription = (t: Translate, item: ManagedConfiguration): string =>
  t(`descriptions.${item.name}`);

export const allowedValueLabel = (t: Translate, item: ManagedConfiguration, value: string): string =>
  t(`allowedValues.${item.name}.${value}`, { defaultValue: enumLabel(item.name, value) ?? value });

export const CONFIGURATION_PAGE_SIZE = 20;

const displayValue = (t: Translate, item: ManagedConfiguration, value?: string): string => {
  if (!value) return t('notSet');
  if (item.valueType === 'Boolean') return value === 'true' ? t('yes') : t('no');
  if (item.valueType === 'Dropdown') return allowedValueLabel(t, item, value);
  return value;
};

interface ResetOption {
  id: string;
  label: string;
  onSelect: () => void;
}

// One reset target is a plain button; two open a menu so each option can name the value it restores.
const ResetAction = ({ label, options, disabled }: { label: string; options: ResetOption[]; disabled: boolean }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (options.length === 1) {
    return (
      <Button
        variant="secondary"
        size="md"
        disabled={disabled}
        onClick={options[0].onSelect}
      >
        {options[0].label}
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="secondary"
        size="md"
        disabled={disabled}
        aria-haspopup="menu"
        onClick={event => setAnchor(event.currentTarget)}
      >
        {label}
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        {options.map(option => (
          <MenuItem
            key={option.id}
            onClick={() => {
              setAnchor(null);
              option.onSelect();
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

interface BuildColumnsOptions {
  scopeFor: (item: ManagedConfiguration) => ConfigurationScope;
  savingName?: string;
  retryNames?: ReadonlySet<string>;
  drafts: Record<string, string>;
  setDraft: (name: string, value: string) => void;
  showCustomerManageable: boolean;
  onSave: (item: ManagedConfiguration, value?: string, removeOverride?: boolean) => void;
  onRetry?: (item: ManagedConfiguration) => void;
  onResetToDefault?: (item: ManagedConfiguration) => void;
  onClearSecret?: (item: ManagedConfiguration) => void;
  onViewAudit?: (item: ManagedConfiguration) => void;
  onConfigureSpecialist?: (item: ManagedConfiguration) => void;
  cleanupStaleOverrides?: boolean;
  t: Translate;
}

/**
 * Drafts are what the user has typed but not saved. The page clears one after a successful save
 * or reset so the row falls back to the server value, and clears all of them on a scope change so
 * a value typed for one store is never shown or saved for another.
 */
const useDrafts = () => {
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const setDraft = useCallback((name: string, value: string) =>
    setDrafts(current => ({ ...current, [name]: value })), []);
  const clearDraft = useCallback((name: string) =>
    setDrafts(current => {
      if (!(name in current)) return current;
      const next = { ...current };
      delete next[name];
      return next;
    }), []);
  const clearDrafts = useCallback(() => setDrafts({}), []);

  return { drafts, setDraft, clearDraft, clearDrafts };
};

const canEditAtScope = (item: ManagedConfiguration, scope: ConfigurationScope): boolean =>
  scope === 'Store' ? item.canSetAtStore : item.canSetAtFranchise;

const buildColumns = ({
  scopeFor,
  savingName,
  retryNames,
  drafts,
  setDraft,
  showCustomerManageable,
  onSave,
  onRetry,
  onResetToDefault,
  onClearSecret,
  onViewAudit,
  onConfigureSpecialist,
  cleanupStaleOverrides,
  t,
}: BuildColumnsOptions): TableColumn<ManagedConfiguration>[] => {
  // A secret's item.value is the mask, never the credential; only what the operator typed is ever sent.
  const getDraft = (item: ManagedConfiguration) =>
    item.isSecret ? (drafts[item.name] ?? '') : (drafts[item.name] ?? item.value ?? '');

  const columns: TableColumn<ManagedConfiguration>[] = [
    {
      id: 'setting',
      width: '22%',
      label: t('columns.setting'),
      sortable: true,
      render: item => (
        <Stack spacing={0.5}>
          <Typography
            variant="textSM"
            fontWeight={600}
          >
            {localisedName(t, item)}
          </Typography>
          <Typography
            variant="textXS"
            color="text.secondary"
          >
            {localisedDescription(t, item)}
          </Typography>
        </Stack>
      ),
    },
    {
      id: 'category',
      width: '10%',
      label: t('columns.category'),
      sortable: true,
      field: 'category',
    },
    {
      id: 'source',
      width: '8%',
      label: t('columns.source'),
      render: item => (
        <Chip
          label={t(`sources.${item.source}`)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      id: 'value',
      width: '26%',
      label: t('columns.value'),
      render: item => {
        const disabled = !canEditAtScope(item, scopeFor(item));
        const value = getDraft(item);
        const name = localisedName(t, item);

        // Composed values keep their own editor; the definition still governs scope and permission.
        if (hasSpecialistEditor(item.name)) {
          return (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography variant="textSM">{item.value || t('pos.notConfigured')}</Typography>
              <Button
                variant="secondary"
                size="md"
                disabled={disabled || savingName === item.name}
                onClick={() => onConfigureSpecialist?.(item)}
              >
                {t('actions.configure')}
              </Button>
            </Stack>
          );
        }

        if (item.valueType === 'Boolean') {
          return (
            <Checkbox
              checked={value === 'true'}
              disabled={disabled}
              onChange={event => setDraft(item.name, String(event.target.checked))}
              inputProps={{ 'aria-label': name }}
            />
          );
        }

        if (item.valueType === 'Dropdown' && item.allowedValues.length > 0) {
          return (
            <TextField
              select
              size="small"
              disabled={disabled}
              value={value}
              onChange={event => setDraft(item.name, event.target.value)}
              sx={{ minWidth: 180, mb: 0 }}
              inputProps={{ 'aria-label': name }}
            >
              {dropdownOptions(item.name, item.allowedValues).map(option => (
                <MenuItem
                  value={option}
                  key={option}
                >
                  {allowedValueLabel(t, item, option)}
                </MenuItem>
              ))}
            </TextField>
          );
        }

        return (
          <TextField
            size="small"
            disabled={disabled}
            type={item.isSecret ? 'password' : item.valueType === 'Number' ? 'number' : 'text'}
            autoComplete={item.isSecret ? 'new-password' : undefined}
            value={item.isSecret ? (drafts[item.name] ?? '') : value}
            placeholder={item.isSecret
              ? (item.isConfigured ? t('secretConfigured') : t('secretNotConfigured'))
              : undefined}
            onChange={event => setDraft(item.name, event.target.value)}
            sx={{ minWidth: 180, mb: 0 }}
            inputProps={{ 'aria-label': name }}
          />
        );
      },
    },
  ];

  // Read-only: whether a setting is delegated is decided in the catalogue, not on this screen.
  if (showCustomerManageable) {
    columns.push({
      id: 'customerManageable',
      width: '8%',
      label: t('columns.customerManageable'),
      render: item => (
        <Typography variant="textSM">
          {item.isCustomerManageable ? t('yes') : t('no')}
        </Typography>
      ),
    });
  }

  columns.push({
    id: 'actions',
    width: '26%',
    label: t('columns.actions'),
    render: item => {
      const scope = scopeFor(item);
      const editable = canEditAtScope(item, scope);
      const saving = savingName === item.name;
      // Save is inert until something changed, except after a write whose downstream refresh failed:
      // Save then replays that same write, whatever it was, so the refresh is retried.
      const draft = drafts[item.name];
      const valueDirty = draft !== undefined && draft !== (item.isSecret ? '' : (item.value ?? ''));
      const canRetry = onRetry !== undefined && retryNames?.has(item.name) === true;

      // Removing a Store value restores what the store inherits, which is the definition default when
      // no Franchise or System value exists. It stays available after a reset has written the default
      // into the Store row, so that row can always go back to inheriting.
      const resetOptions: ResetOption[] = [];
      // Removing a Store override of a definition that is no longer editable at Store scope is
      // administrative cleanup, so only a screen that opts in offers it on a non-editable row.
      const canRemoveStoreValue = item.hasStoreOverride && scope === 'Store' && !item.isSecret && (item.canSetAtFranchise || item.canSetAtStore) && (editable || cleanupStaleOverrides === true);
      if (canRemoveStoreValue) {
        resetOptions.push({
          id: 'inherited',
          label: onResetToDefault && item.inheritedValue
            ? t(item.inheritedSource === 'System' ? 'actions.resetToSystem' : 'actions.resetToFranchise', { value: displayValue(t, item, item.inheritedValue) })
            : t('actions.resetInheritance'),
          onSelect: () => onSave(item, undefined, true),
        });
      }
      if (onResetToDefault && item.canResetToDefault) {
        resetOptions.push({
          id: 'default',
          // Without a default the reset can only clear the value, so it says so instead of "default (Not set)".
          label: item.defaultValue
            ? t('actions.resetToDefault', { value: displayValue(t, item, item.defaultValue) })
            : t('actions.clearValue'),
          onSelect: () => onResetToDefault(item),
        });
      }

      return (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          {(!hasSpecialistEditor(item.name) || canRetry) && (
            <Button
              variant="primary"
              size="md"
              disabled={(!valueDirty && !canRetry) || (!editable && !canRetry) || saving}
              onClick={() => (valueDirty ? onSave(item, getDraft(item)) : onRetry?.(item))}
            >
              {t('actions.save')}
            </Button>
          )}
          {resetOptions.length > 0 && (
            <ResetAction
              label={t('actions.reset')}
              options={resetOptions}
              disabled={saving}
            />
          )}
          {item.isSecret && item.isConfigured && onClearSecret && (
            <Button
              variant="bad"
              size="md"
              disabled={(!editable && !item.hasStoreOverride) || saving}
              onClick={() => onClearSecret(item)}
            >
              {t('actions.clearSecret')}
            </Button>
          )}
          {onViewAudit && (
            <Tooltip title={t('actions.history')}>
              <IconButton
                size="small"
                aria-label={t('actions.history')}
                onClick={() => onViewAudit(item)}
              >
                <HistoryOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      );
    },
  });

  return columns;
};

interface ConfigurationTableOptions {
  title: string;
  /** Scope used for every row unless `scopeFor` overrides it per item. */
  scope: ConfigurationScope;
  scopeFor?: (item: ManagedConfiguration) => ConfigurationScope;
  items: readonly ManagedConfiguration[];
  loading: boolean;
  savingName?: string;
  /** Settings whose last write persisted but reported a propagation warning; Save replays it through onRetry. */
  retryNames?: ReadonlySet<string>;
  onRetry?: (item: ManagedConfiguration) => void;
  /** Offer removal of a Store override even where the row is no longer editable at Store scope. */
  cleanupStaleOverrides?: boolean;
  recordCountLabel: string;
  emptyContent?: ReactNode;
  showCustomerManageable?: boolean;
  onSave: (item: ManagedConfiguration, value?: string, removeOverride?: boolean) => void;
  /** Enables resetting to the definition default, and names the value each reset restores. */
  onResetToDefault?: (item: ManagedConfiguration) => void;
  onClearSecret?: (item: ManagedConfiguration) => void;
  onViewAudit?: (item: ManagedConfiguration) => void;
  onConfigureSpecialist?: (item: ManagedConfiguration) => void;
}

export interface ConfigurationTableSection {
  table: TableSectionProps<ManagedConfiguration>;
  clearDraft: (name: string) => void;
  clearDrafts: () => void;
  resetPage: () => void;
}

/**
 * Builds the shared TableSection props so the caller can hand them straight to ListPageTemplate,
 * plus the draft controls the page needs after a save or a scope change. The whole catalogue for a
 * scope is loaded at once, so sorting and paging happen here; filters and search stay with the page.
 */
export const useConfigurationTableSection = ({
  title,
  scope,
  scopeFor,
  items,
  loading,
  savingName,
  retryNames,
  recordCountLabel,
  emptyContent,
  showCustomerManageable = false,
  onSave,
  onRetry,
  onResetToDefault,
  onClearSecret,
  onViewAudit,
  onConfigureSpecialist,
  cleanupStaleOverrides,
}: ConfigurationTableOptions): ConfigurationTableSection => {
  const { t } = useTranslation('manageConfigurations');
  const { drafts, setDraft, clearDraft, clearDrafts } = useDrafts();
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState<string>();
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('asc');

  const columns = buildColumns({
    scopeFor: scopeFor ?? (() => scope),
    savingName,
    retryNames,
    drafts,
    setDraft,
    showCustomerManageable,
    onSave,
    onRetry,
    onResetToDefault,
    onClearSecret,
    onViewAudit,
    onConfigureSpecialist,
    cleanupStaleOverrides,
    t,
  });

  // Unsorted keeps the catalogue order (category, then display order).
  const sortKey = (item: ManagedConfiguration) => (sortBy === 'category' ? item.category : localisedName(t, item));
  const sorted = sortBy
    ? [...items].sort((left, right) => sortKey(left).localeCompare(sortKey(right)) * (sortDirection === 'asc' ? 1 : -1))
    : items;
  const maxPages = Math.max(1, Math.ceil(sorted.length / CONFIGURATION_PAGE_SIZE));
  const currentPage = Math.min(page, maxPages - 1);
  const rows = sorted.slice(currentPage * CONFIGURATION_PAGE_SIZE, (currentPage + 1) * CONFIGURATION_PAGE_SIZE);
  const resetPage = useCallback(() => setPage(0), []);

  return {
    clearDraft,
    clearDrafts,
    resetPage,
    table: {
      title,
      columns,
      // Widths are per column so the layout does not jump between pages as content changes.
      tableSx: { tableLayout: 'fixed' },
      rows,
      getRowId: item => item.name,
      recordCount: items.length,
      loading: loading && items.length === 0,
      loadingOverlay: loading && items.length > 0,
      recordCountLabel,
      emptyContent,
      page: currentPage,
      maxPages,
      onPageChange: setPage,
      sortBy,
      sortDirection,
      onSortChange: (nextSortBy, nextDirection) => {
        setSortBy(nextSortBy);
        setSortDirection(nextDirection);
        setPage(0);
      },
      renderBody: pageRows => (
        <TableBody>
          {pageRows.map(item => (
            <TableRow key={item.name}>
              {columns.map(column => (
                <TableCell
                  key={column.id}
                  align={column.align}
                >
                  {column.render ? column.render(item, 0) : column.field ? String(item[column.field] ?? '') : null}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      ),
    },
  };
};
